import { FileText } from 'lucide-react';

// O balao de DOCUMENTO do AppCel ([DA-572]).
//
// Peca unica de proposito: ela desenha o mesmo cartao no chat do CLIENTE (`ChatApp`) e
// no chat da LOJA pelo celular (`ChatAtender`). Duas copias divergiriam na primeira
// mexida, e ai o cliente e a vendedora estariam olhando coisas diferentes com o mesmo
// nome — que e' exatamente o buraco que a [DA-540] deixou aberto ate aqui (o lado da
// loja mostrava `[document] — abra no sistema para ver`).
//
// ⚠️ **O toque ABRE, e a abertura acontece DENTRO do clique.** Nada de `await` antes do
// `window.open`: qualquer espera gasta o gesto e o bloqueador de pop-up entra no meio —
// a mesma armadilha que o `BotaoCompartilhar` e o PDF da nota ja documentam. Por isso
// aqui nao ha download nem montagem de blob: o arquivo ja esta no servidor, e o
// endereco dele basta. Quem quiser SALVAR ou mandar adiante usa o botao de
// compartilhar, que fica ao lado do balao e ja e' peca da casa ([DA-524]).
//
// ⚠️ **Nome de arquivo e' o conteudo, nao enfeite.** Numa conversa com varios PDFs
// ("Extrato - ILUMINAR.pdf", "Orcamento 6823.pdf") o rotulo generico "Documento" torna
// os dois indistinguiveis — e quem recebe cobranca precisa saber qual abriu. Por isso o
// `midia_nome` viaja do banco ate aqui.

/** Bytes em algo que uma pessoa le. Sem casa decimal em KB: precisao ali nao informa nada. */
function tamanhoLegivel(bytes: number | null | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}

/** O que se mostra quando o nome nao veio (mensagem antiga, anexo sem nome). */
const NOME_PADRAO = 'Documento';

interface Props {
  url: string;
  nome?: string | null;
  bytes?: number | null;
  /** `true` = balao da propria pessoa (fundo quente); muda so a moldura. */
  meu: boolean;
}

export function AnexoDocumento({ url, nome, bytes, meu }: Props) {
  const rotulo = (nome ?? '').trim() || NOME_PADRAO;
  const tamanho = tamanhoLegivel(bytes);

  function abrir(): void {
    // Aba nova mantem o app aberto atras — no iPhone o PDF cai no Safari e o AppCel
    // continua onde estava. Bloqueador de pop-up devolve `null`; ai navegar a propria
    // aba e' o que sobra (o cliente volta com o botao de voltar do aparelho).
    //
    // ⛔ **`window.open(url, '_blank', 'noopener')` devolve `null` SEMPRE** — nao e'
    // falha, e' a especificacao: com `noopener` nao ha referencia pra devolver. Escrito
    // assim, o `if (!aba)` disparava a cada toque e o PDF abria DUAS vezes — numa aba
    // nova **e** por cima do proprio app, que e' justamente o que a aba nova existe pra
    // evitar. Medido apertando o botao na tela publicada ([DA-572]); nenhum portao pega
    // isso, e lendo o codigo ele parece correto. **O `noopener` vem depois, na mao.**
    const aba = window.open(url, '_blank');
    if (!aba) {
      window.location.href = url;
      return;
    }
    // Mesma protecao do `noopener` sem perder o retorno: a pagina aberta nao alcanca
    // a nossa. (Aqui e' PDF do proprio dominio, mas a regra nao depende do conteudo.)
    aba.opener = null;
  }

  return (
    <button
      type="button"
      onClick={abrir}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        borderRadius: 11,
        // Contraste dentro do proprio balao: o cartao precisa se ler como uma COISA
        // anexada, nao como texto. Claro sobre o balao quente, cinza sobre o branco.
        backgroundColor: meu ? 'rgba(255,255,255,0.55)' : '#f5f5f4',
        border: '1px solid #e7e5e4',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: 9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          border: '1px solid #e7e5e4',
          color: '#b45309',
        }}
      >
        <FileText size={20} />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            color: '#292524',
            lineHeight: 1.3,
            // Nome longo QUEBRA em vez de estourar o balao. Cortar com reticencias
            // esconderia justamente o fim, que e' onde mora o que distingue dois
            // arquivos parecidos ("Extrato - ILUMINAR" vs "Extrato - ERNANE").
            overflowWrap: 'anywhere',
          }}
        >
          {rotulo}
        </span>
        {/* ⚠️ **Duas linhas, nao uma frase so.** Medido no ar em 390px (iPhone): o balao
            de mensagem da loja e' limitado a 72% da tela pra caber os dois botoes ao
            lado, o que deixa o cartao com **227px**; numa linha unica o texto quebrava
            no meio da frase ("Toque para / abrir"). Separando, cada pedaco cabe inteiro
            em qualquer largura. */}
        <span style={{ display: 'block', fontSize: 12, color: '#78716c', marginTop: 2 }}>
          {tamanho ? `PDF · ${tamanho}` : 'PDF'}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: '#a8a29e', marginTop: 1 }}>
          Toque para abrir
        </span>
      </span>
    </button>
  );
}
